import React from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { useGoogleAuth } from "./hooks/useGoogleAuth";
import { useWebSocket } from "./hooks/useWebSocket";
import { useEffect } from "react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CreatePassword from "@/pages/CreatePassword";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CandidateDashboard from "@/pages/CandidateDashboard";
import TrainingModules from "@/pages/TrainingModules";
import Quiz from "@/pages/Quiz";
import Feedback from "@/pages/Feedback";
import ManageQuizzes from "@/pages/ManageQuizzes";
import Analytics from "@/pages/Analytics";
import ManageUsers from "@/pages/ManageUsers";
import AddEmployee from "@/pages/AddEmployee";
import AddCandidate from "@/pages/AddCandidate";
import AddTrainingModule from "@/pages/AddTrainingModule";
import AddFeedbackForm from "@/pages/AddFeedbackForm";
import AddTeamImage from "@/pages/AddTeamImage";
import TeamImages from "@/pages/TeamImages";
import EditTeamImage from "@/pages/EditTeamImage";
import BulkUpload from "@/pages/BulkUpload";
import EmployeeTrainingModules from "@/pages/EmployeeTrainingModules";
import KnowledgeHub from "@/pages/KnowledgeHub";
import Settings from "@/pages/Settings";
import Progress from "@/pages/Progress";
import AdminReminderContent from "@/pages/AdminReminderContent";
import MeetBuddy from "@/pages/MeetBuddy";
import MeetTeam from "@/pages/MeetTeam";
import MeetTheTeam from "@/pages/MeetTheTeam";
import OfficeLocations from "@/pages/OfficeLocations";
import Helpdesk from "@/pages/Helpdesk";
import AIAgent from "@/pages/AIAgent";
import SlackIntegration from "@/pages/SlackIntegration";
import Profile from "@/pages/Profile";
import NotFound from "@/pages/not-found";
import Layout from "@/components/Layout";

function AuthenticatedApp() {
  // Decide when to check session
  const params = new URLSearchParams(window.location.search);
  const isGoogleCallback = params.get('google_auth') === 'success';
  const path = window.location.pathname;
  
  // IMPORTANT: '/' is NOT a public route when user is authenticated
  // It's the dashboard route. Only treat it as public if we're checking for initial auth
  // We need to check session for '/' because it could be the employee dashboard
  const isPublicRoute = (
    path === '/login' ||
    path === '/forgot-password' ||
    path.startsWith('/reset-password') ||
    path.startsWith('/create-password')
  );
  
  // Always check session unless it's a known public route (but not '/')
  // '/' needs session check because it's the dashboard route
  // For public routes like /create-password, completely skip auth check
  const shouldCheckSession = isGoogleCallback || !isPublicRoute;

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['/auth/me'],
    enabled: shouldCheckSession,
    // For public routes, don't even attempt to fetch - this prevents 401 errors
    // React Query will not make the request if enabled is false
    retry: false, // Disable retry for public routes - if enabled is false, this won't matter anyway
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle Google OAuth callback
  useGoogleAuth();
  
  // Initialize WebSocket connection for real-time notifications
  // Completely isolated - errors won't affect anything
  let webSocketHook: ReturnType<typeof useWebSocket>;
  try {
    webSocketHook = useWebSocket();
  } catch (wsError) {
    // If WebSocket hook fails, use safe defaults
    webSocketHook = {
      isConnected: false,
      connectionStatus: 'disconnected',
      connect: () => {},
      disconnect: () => {},
    };
  }
  
  // Don't log WebSocket status - it's not important and causes noise

  // Force exit any picture-in-picture videos globally
  useEffect(() => {
    const forceExitPictureInPicture = async () => {
      try {
        // Exit any current picture-in-picture
        if (document.pictureInPictureElement) {
          console.log('Exiting picture-in-picture mode');
          await document.exitPictureInPicture();
        }
        
        // Find and disable all video elements
        const videos = document.querySelectorAll('video');
        videos.forEach(video => {
          video.disablePictureInPicture = true;
          // Force exit if in PiP
          if (video === document.pictureInPictureElement) {
            document.exitPictureInPicture().catch(() => {});
          }
        });
      } catch (error) {
        console.log('Picture-in-picture cleanup:', error);
      }
    };

    // Run immediately
    forceExitPictureInPicture();

    // Prevent any future picture-in-picture activations
    const preventPictureInPicture = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Prevented picture-in-picture activation');
    };

    // Listen for picture-in-picture events
    document.addEventListener('enterpictureinpicture', preventPictureInPicture, true);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('enterpictureinpicture', preventPictureInPicture, true);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // For public routes, always show them without checking auth
  if (isPublicRoute) {
    return (
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/create-password" component={CreatePassword} />
        <Route component={() => {
          // Redirect any other route to landing page
          window.location.href = '/';
          return null;
        }} />
      </Switch>
    );
  }

  // If user is not authenticated, show public routes (landing and login)
  // BUT: Don't show landing page if we're processing a Google SSO callback
  // Keep showing loading state while SSO callback is being processed
  if ((error || !user) && !isGoogleCallback) {
    return (
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/create-password" component={CreatePassword} />
        <Route component={() => {
          // Redirect any other route to landing page when not authenticated
          window.location.href = '/';
          return null;
        }} />
      </Switch>
    );
  }

  // If we're processing a Google callback but user data isn't loaded yet, keep showing loading
  if (isGoogleCallback && (!user || isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-servify-purple mx-auto mb-4"></div>
          <p className="text-gray-600">Completing login...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={(user as any)?.user} showChatbot={(user as any)?.user?.role !== 'admin'}>
      <Switch>
        <Route path="/" component={() => {
          switch ((user as any)?.user?.role) {
            case 'admin':
              return <AdminDashboard />;
            case 'candidate':
              return <CandidateDashboard />;
            default:
              return <EmployeeDashboard />;
          }
        }} />
        <Route path="/admin" component={() => {
          // Redirect admin users to admin dashboard, others to their appropriate dashboard
          if ((user as any)?.user?.role === 'admin') {
            return <AdminDashboard />;
          } else {
            // Redirect to appropriate dashboard based on role
            window.location.href = '/';
            return null;
          }
        }} />
        <Route path="/candidate" component={() => {
          // Redirect candidate users to candidate dashboard, others to their appropriate dashboard
          if ((user as any)?.user?.role === 'candidate') {
            return <CandidateDashboard />;
          } else {
            // Redirect to appropriate dashboard based on role
            window.location.href = '/';
            return null;
          }
        }} />
        <Route path="/training" component={() => {
          // Route admins to admin training page, employees to employee training page
          if ((user as any)?.user?.role === 'admin') {
            return <TrainingModules />;
          } else {
            return <EmployeeTrainingModules />;
          }
        }} />
        <Route path="/quiz/:id" component={Quiz} />
        <Route path="/feedback" component={Feedback} />
                       <Route path="/quiz/manage" component={ManageQuizzes} />
               <Route path="/analytics" component={Analytics} />
               <Route path="/users" component={ManageUsers} />
        <Route path="/add-employee" component={AddEmployee} />
        <Route path="/add-candidate" component={AddCandidate} />
        <Route path="/bulk-upload" component={BulkUpload} />
        <Route path="/add-training-module" component={AddTrainingModule} />
        <Route path="/team-images" component={TeamImages} />
        <Route path="/add-team-image" component={AddTeamImage} />
        <Route path="/edit-team-image/:id" component={EditTeamImage} />
        <Route path="/admin/add-feedback-form" component={AddFeedbackForm} />
        <Route path="/employee-training" component={EmployeeTrainingModules} />
        <Route path="/knowledge-hub" component={KnowledgeHub} />
        <Route path="/meet-buddy" component={MeetBuddy} />
        <Route path="/meet-team" component={MeetTeam} />
        <Route path="/meet-the-team" component={MeetTheTeam} />
        <Route path="/office-locations" component={OfficeLocations} />
        <Route path="/helpdesk" component={Helpdesk} />
        <Route path="/reminder-content" component={AdminReminderContent} />
        <Route path="/ai-agent" component={AIAgent} />
        <Route path="/slack-integration" component={() => {
          // Route admins to Slack integration page
          if ((user as any)?.user?.role === 'admin') {
            return <SlackIntegration />;
          } else {
            // Redirect non-admins to dashboard
            window.location.href = '/';
            return null;
          }
        }} />
        <Route path="/settings" component={Settings} />
        <Route path="/progress" component={Progress} />
        <Route path="/profile" component={Profile} />
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <AuthenticatedApp />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
